import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { format } from 'date-fns'
import {
    ChevronLeft,
    ChevronRight,
    AlertTriangle,
    Loader2,
    RefreshCw,
    X,
    Package,
    MapPin,
    ShieldAlert,
    TrendingDown,
    EyeOff,
    Store,
    BarChart3,
    Activity,
    ArrowRight,
    Clock,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn, stripBrandPrefixFromProductName } from '@/lib/utils'
import { useAvailability } from '@/hooks/useAvailability'
import { useSettings } from '@/contexts/SettingsContext'
import { useTopBar } from '@/contexts/TopBarContext'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import { trpc } from '@/lib/trpc'
import { MultiSelectCityFilter } from '@/components/ui/MultiSelectCityFilter'
import { TopBarFilterDropdown } from '@/components/ui/TopBarFilterDropdown'
import { DateRangePicker } from '@/components/ui/DateRangePicker'
import type {
    SKUAvailability,
    CategoryAvailability,
    CityAvailability,
    CityAvailabilityDetail,
    DarkStoreAvailability,
    CityFocusResponse,
    PSLLevel,
    ListingIssueType,
    AvailabilityFilters,
    CustomDateRange,
    PendingSKU,
} from '@/types'
import type { DateRange } from 'react-day-picker'

// ============================================================================
// CONSTANTS
// ============================================================================

const platformOptions = [
    { label: 'All Platforms', value: 'all' },
    { label: 'Blinkit', value: 'blinkit' },
    { label: 'Zepto', value: 'zepto' },
    { label: 'Instamart', value: 'instamart' },
    { label: 'BBNow', value: 'bbnow' },
]

const skuOptions = [
    { label: 'All SKUs', value: 'all' },
    { label: 'Top 10 SKUs', value: 'top10' },
    { label: 'Top 50 SKUs', value: 'top50' },
    { label: 'Critical SKUs', value: 'critical' },
]

const MAX_VISIBLE_IMPACTED = 4

function getDummyDOI(seed: string): number {
    let hash = 0
    for (let i = 0; i < seed.length; i++) {
        hash = ((hash << 5) - hash) + seed.charCodeAt(i)
        hash |= 0
    }
    return Number((0.8 + (Math.abs(hash) % 40) / 10).toFixed(1))
}

// ============================================================================
// SMALL UI PRIMITIVES
// ============================================================================

const PSL_STYLES: Record<PSLLevel, { color: string; bg: string; dot: string }> = {
    High: { color: 'text-red-700 dark:text-red-400', bg: 'bg-red-500/10', dot: 'bg-red-500' },
    Medium: { color: 'text-amber-700 dark:text-amber-400', bg: 'bg-amber-500/10', dot: 'bg-amber-500' },
    Low: { color: 'text-emerald-700 dark:text-emerald-400', bg: 'bg-emerald-500/10', dot: 'bg-emerald-500' },
}

const ISSUE_STYLES: Record<string, { color: string; bg: string }> = {
    'Suppressed': { color: 'text-red-700 dark:text-red-400', bg: 'bg-red-500/10' },
    'Missing Title': { color: 'text-amber-700 dark:text-amber-400', bg: 'bg-amber-500/10' },
    'Missing Image': { color: 'text-purple-700 dark:text-purple-400', bg: 'bg-purple-500/10' },
    'Wrong Category': { color: 'text-orange-700 dark:text-orange-400', bg: 'bg-orange-500/10' },
    'Inactive': { color: 'text-gray-700 dark:text-gray-400', bg: 'bg-gray-500/10' },
    'Out of Stock': { color: 'text-red-700 dark:text-red-400', bg: 'bg-red-500/10' },
    'Delisted': { color: 'text-rose-700 dark:text-rose-400', bg: 'bg-rose-500/10' },
}

function PSLPill({ level }: { level?: PSLLevel }) {
    if (!level) return null
    const s = PSL_STYLES[level]
    return (
        <span className={cn("inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg text-xs font-semibold", s.bg, s.color)}>
            <span className={cn("w-1.5 h-1.5 rounded-full", s.dot)} />
            {level}
        </span>
    )
}

function IssuePill({ issue }: { issue: ListingIssueType }) {
    const s = ISSUE_STYLES[issue] || { color: 'text-muted-foreground', bg: 'bg-muted' }
    return (
        <span className={cn("px-2.5 py-0.5 rounded-lg text-xs font-semibold", s.bg, s.color)}>
            {issue}
        </span>
    )
}

function OSAValue({ value, size = 'lg' }: { value: number; size?: 'sm' | 'lg' }) {
    return (
        <span className={cn(
            "font-bold leading-none",
            size === 'lg' ? "text-lg" : "text-sm",
            value >= 80 ? "text-emerald-700 dark:text-emerald-400" :
                value >= 50 ? "text-amber-700 dark:text-amber-400" :
                    "text-red-700 dark:text-red-400"
        )}>
            {value.toFixed(0)}%
        </span>
    )
}

// ============================================================================
// GLASS CARD PRIMITIVES (exact CompetitorPage design language)
// ============================================================================

const GLASS_CARD = [
    "rounded-2xl",
    "bg-white/60 dark:bg-white/[0.08]",
    "backdrop-blur-xl backdrop-saturate-[1.4]",
    "border border-white/50 dark:border-white/[0.12]",
    "shadow-[0_2px_16px_rgba(0,0,0,0.06),inset_0_1px_0_0_rgba(255,255,255,0.6)]",
    "dark:shadow-[0_2px_16px_rgba(0,0,0,0.3),inset_0_1px_0_0_rgba(255,255,255,0.08)]",
    "transition-shadow duration-200 ease-out",
].join(' ')

const GLASS_HOVER = [
    "hover:shadow-[0_8px_30px_rgba(0,0,0,0.1),inset_0_1px_0_0_rgba(255,255,255,0.7)]",
    "hover:border-primary/20",
].join(' ')

const GLASS_SELECTED = [
    "ring-[1.5px] ring-primary/50",
    "bg-white/70 dark:bg-white/[0.12]",
    "shadow-[0_8px_30px_rgba(0,0,0,0.1),inset_0_1px_0_0_rgba(255,255,255,0.7)]",
].join(' ')

const GLASS_DIVIDER = "h-px bg-gradient-to-r from-transparent via-black/[0.06] dark:via-white/[0.08] to-transparent"

const GLASS_INNER = "rounded-xl bg-white/40 dark:bg-white/[0.06] border border-white/40 dark:border-white/[0.08]"

// ============================================================================
// SLIDER CAROUSEL — Reusable horizontal slider with left/right arrows
// ============================================================================

function SliderCarousel({ children, className }: { children: React.ReactNode; className?: string }) {
    const scrollRef = useRef<HTMLDivElement>(null)
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)

    const checkScroll = useCallback(() => {
        const el = scrollRef.current
        if (!el) return
        setCanScrollLeft(el.scrollLeft > 4)
        setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4)
    }, [])

    useEffect(() => {
        const el = scrollRef.current
        if (!el) return
        checkScroll()
        el.addEventListener('scroll', checkScroll, { passive: true })
        const ro = new ResizeObserver(checkScroll)
        ro.observe(el)
        return () => { el.removeEventListener('scroll', checkScroll); ro.disconnect() }
    }, [checkScroll])

    // Also recheck when children change (new data loaded)
    useEffect(() => { checkScroll() }, [children, checkScroll])

    const scroll = (direction: 'left' | 'right') => {
        const el = scrollRef.current
        if (!el) return
        const scrollAmount = el.clientWidth * 0.7
        el.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' })
    }

    return (
        <div className="relative group/slider">
            {/* Left arrow */}
            {canScrollLeft && (
                <button
                    onClick={() => scroll('left')}
                    className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-background/90 backdrop-blur-sm border border-border/60 shadow-lg flex items-center justify-center text-foreground hover:bg-background transition-opacity duration-150 -ml-3 opacity-0 group-hover/slider:opacity-100"
                >
                    <ChevronLeft className="h-5 w-5" />
                </button>
            )}

            {/* Scrollable content */}
            <div
                ref={scrollRef}
                className={cn("flex overflow-x-auto scrollbar-none scroll-smooth", className)}
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
                {children}
            </div>

            {/* Right arrow */}
            {canScrollRight && (
                <button
                    onClick={() => scroll('right')}
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-background/90 backdrop-blur-sm border border-border/60 shadow-lg flex items-center justify-center text-foreground hover:bg-background transition-opacity duration-150 -mr-3 opacity-0 group-hover/slider:opacity-100"
                >
                    <ChevronRight className="h-5 w-5" />
                </button>
            )}

            {/* Left fade gradient */}
            {canScrollLeft && (
                <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-[#FDFDFD] dark:from-[#0A0A0A] to-transparent pointer-events-none z-10" />
            )}

            {/* Right fade gradient */}
            {canScrollRight && (
                <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-[#FDFDFD] dark:from-[#0A0A0A] to-transparent pointer-events-none z-10" />
            )}
        </div>
    )
}

// ============================================================================
// IMPACTED SKU — COMPACT HORIZONTAL CARD (for the top strip)
// ============================================================================

function ImpactedSKUCompactCard({ sku, mode, onClick, brand, fullWidth }: {
    sku: SKUAvailability
    mode: 'listing' | 'psl'
    onClick: () => void
    brand: string
    fullWidth?: boolean
}) {
    const issueCount = sku.issues.length
    const osaValue = sku.weightedOsa ?? sku.osa ?? 0

    return (
        <div
            onClick={onClick}
            className={cn("group/card relative overflow-hidden cursor-pointer flex-shrink-0", fullWidth ? "w-full" : "w-[320px]", GLASS_CARD, GLASS_HOVER)}
        >
            {sku.imageUrl && (
                <img src={sku.imageUrl} alt="" aria-hidden
                    className="absolute -bottom-6 -right-6 w-36 h-36 object-contain opacity-[0.06] blur-lg scale-110 pointer-events-none select-none"
                    loading="lazy" />
            )}

            <div className="relative z-[1] flex flex-col">
                {/* Header */}
                <div className="p-3.5 pb-2.5">
                    <div className="flex gap-2.5 items-start">
                        <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center bg-white/70 dark:bg-white/10 border border-white/60 dark:border-white/10">
                            {sku.imageUrl ? (
                                <img src={sku.imageUrl} alt={sku.name} className="w-full h-full object-contain scale-125" loading="lazy"
                                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }} />
                            ) : (
                                <Package className="h-4 w-4 text-muted-foreground" />
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="module-product-title line-clamp-1" title={sku.name}>
                                {stripBrandPrefixFromProductName(sku.name, sku.brand || brand)}
                            </h3>
                            <p className="module-product-subtitle mt-0.5 truncate">{sku.category}</p>
                        </div>
                        {mode === 'psl' && <PSLPill level={sku.psl} />}
                    </div>
                </div>

                {/* Glass divider */}
                <div className={cn("mx-3.5", GLASS_DIVIDER)} />

                {/* Metric row */}
                <div className="px-3.5 pt-2.5 pb-1.5">
                    <div className="grid grid-cols-3 gap-2">
                        <div className={cn("p-2 rounded-lg text-center", GLASS_INNER)}>
                            <span className="module-metric-kicker block mb-0.5">OSA</span>
                            <OSAValue value={osaValue} />
                        </div>
                        <div className={cn("p-2 rounded-lg text-center", GLASS_INNER)}>
                            <span className="module-metric-kicker block mb-0.5">Listing</span>
                            <span className="module-metric-value-sm">{(sku.listingPercentage ?? 100).toFixed(0)}%</span>
                        </div>
                        <div className={cn("p-2 rounded-lg text-center", GLASS_INNER)}>
                            <span className="module-metric-kicker block mb-0.5">DOI</span>
                            <span className="module-metric-value-sm">{getDummyDOI(sku.id)}d</span>
                        </div>
                    </div>
                </div>

                {/* Bottom info row */}
                <div className="px-3.5 pb-3 pt-1.5">
                    {mode === 'listing' && issueCount > 0 ? (
                        <div className="flex items-center gap-1.5 overflow-hidden">
                            {sku.issues.includes('Out of Stock') ? (
                                <span className="px-2.5 py-0.5 rounded-lg text-xs font-semibold bg-red-500/10 text-red-700">
                                    Impacted across {sku.affectedStores} darkstores
                                </span>
                            ) : (
                                <>
                                    {sku.issues.slice(0, 2).map((issue, idx) => (
                                        <IssuePill key={idx} issue={issue} />
                                    ))}
                                    {sku.issues.length > 2 && (
                                        <span className="text-xs font-semibold text-muted-foreground flex-shrink-0">+{sku.issues.length - 2}</span>
                                    )}
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="flex items-center gap-3 text-xs text-muted-foreground font-medium">
                            <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{sku.affectedCities} cities</span>
                            <span className="flex items-center gap-1"><Store className="h-3 w-3" />{sku.affectedStores} stores</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

// ============================================================================
// PENDING SKU CARD — Tracked product awaiting scrape/data
// ============================================================================

function PendingSKUCard({ sku, brand }: { sku: PendingSKU; brand: string }) {
    const displayName = sku.brand
        ? sku.name.replace(new RegExp(`^${sku.brand}\\s*`, 'i'), '')
        : sku.name.replace(new RegExp(`^${brand}\\s*`, 'i'), '')

    return (
        <div className={cn(
            "group/card relative overflow-hidden flex-shrink-0 w-[320px]",
            "rounded-2xl",
            "bg-white/40 dark:bg-white/[0.04]",
            "backdrop-blur-xl backdrop-saturate-[1.4]",
            "border border-dashed border-muted-foreground/25 dark:border-white/[0.15]",
            "shadow-[0_2px_16px_rgba(0,0,0,0.03)]",
            "dark:shadow-[0_2px_16px_rgba(0,0,0,0.15)]",
            "transition-shadow duration-200 ease-out",
        )}>
            <div className="relative z-[1] flex flex-col">
                {/* Header */}
                <div className="p-3.5 pb-2.5">
                    <div className="flex gap-2.5 items-start">
                        <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center bg-white/50 dark:bg-white/[0.06] border border-dashed border-muted-foreground/20 dark:border-white/[0.1]">
                            {sku.imageUrl ? (
                                <img src={sku.imageUrl} alt={sku.name} className="w-full h-full object-contain scale-125 opacity-50 grayscale-[40%]" loading="lazy"
                                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }} />
                            ) : (
                                <Package className="h-4 w-4 text-muted-foreground/50" />
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="module-product-title text-muted-foreground line-clamp-1" title={sku.name}>
                                {displayName}
                            </h3>
                            <p className="module-product-subtitle text-muted-foreground/60 mt-0.5 truncate">
                                {sku.category || 'Uncategorized'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Glass divider */}
                <div className={cn("mx-3.5", GLASS_DIVIDER)} />

                {/* Pending status area */}
                <div className="px-3.5 pt-3 pb-3">
                    <div className={cn(
                        "flex flex-col items-center justify-center gap-2 py-4 rounded-xl",
                        "bg-amber-500/[0.06] dark:bg-amber-500/[0.08]",
                        "border border-amber-500/10 dark:border-amber-500/15",
                    )}>
                        <div className="relative">
                            <div className="w-8 h-8 rounded-full bg-amber-500/10 flex items-center justify-center">
                                <Clock className="h-4 w-4 text-amber-600 dark:text-amber-400 animate-pulse" />
                            </div>
                        </div>
                        <div className="text-center px-3">
                            <p className="text-xs font-semibold text-amber-700 dark:text-amber-400">
                                Awaiting Data
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                                Data will be available after the next scrape
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

// ============================================================================
// SHOW MORE SIDEBAR — Full impacted SKU list with city breakdowns
// ============================================================================

// @ts-ignore -- sidebar replaced by inline expansion; kept for potential reuse
function ImpactedSKUsSidebar({
    isOpen,
    skus,
    mode,
    brand,
    onClose,
}: {
    isOpen: boolean
    skus: SKUAvailability[]
    mode: 'listing' | 'psl'
    brand: string
    onClose: () => void
}) {
    if (!isOpen) return null

    return createPortal(
        <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/80 animate-in fade-in duration-300" onClick={onClose} />

            <div className="absolute right-0 top-0 h-full w-full lg:w-[60vw] max-w-3xl bg-background shadow-2xl border-l border-border/40 flex flex-col animate-in slide-in-from-right duration-400 ease-out sm:rounded-l-3xl overflow-hidden">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-6 sm:px-10 py-6 sm:py-8 border-b border-border/40 bg-card/50 backdrop-blur-xl flex-shrink-0 z-10">
                    <div className="flex items-center gap-5 flex-1 min-w-0">
                        <div className="relative">
                            <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full" />
                            <div className="relative w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center border border-primary/20 shadow-lg">
                                <ShieldAlert className="w-7 h-7 text-primary" />
                            </div>
                        </div>
                        <div className="min-w-0 flex-1">
                            <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider rounded-md mb-2">
                                {mode === 'listing' ? 'Listing Issues' : 'Top PSL'}
                            </span>
                            <h3 className="text-2xl sm:text-3xl font-bold text-foreground leading-tight">
                                All Impacted SKUs
                            </h3>
                            <p className="text-base font-semibold text-muted-foreground mt-1">
                                {skus.length} SKUs with {mode === 'listing' ? 'catalog failures' : 'availability severity'}
                            </p>
                        </div>
                    </div>

                    <Button variant="secondary" size="icon"
                        className="rounded-xl hover:bg-destructive hover:text-destructive-foreground transition-colors h-9 w-9 flex-shrink-0 shadow-sm"
                        onClick={onClose}>
                        <X className="h-4 w-4" />
                    </Button>
                </div>

                {/* Scrollable list */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                    <div className="space-y-2">
                        {skus.map((sku) => (
                            <SidebarSKURow key={sku.id} sku={sku} mode={mode} brand={brand} />
                        ))}
                        {skus.length === 0 && (
                            <p className="text-center text-muted-foreground py-8">No impacted SKUs found</p>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    )
}

function SidebarSKURow({ sku, mode, brand }: { sku: SKUAvailability; mode: 'listing' | 'psl'; brand: string }) {
    const [expanded, setExpanded] = useState(false)
    const hasCities = sku.cityWiseIssues.length > 0

    return (
        <div className={cn("overflow-hidden", GLASS_CARD)}>
            <button
                onClick={() => hasCities && setExpanded(!expanded)}
                className={cn("w-full flex items-center gap-3 p-3 text-left transition-colors", hasCities && "hover:bg-white/30 dark:hover:bg-white/5 cursor-pointer")}
            >
                {hasCities && (
                    <ChevronRight className={cn("h-4 w-4 text-muted-foreground flex-shrink-0 transition-transform duration-200", expanded && "rotate-90 text-primary")} />
                )}
                <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center bg-white/70 dark:bg-white/10 border border-white/60 dark:border-white/10">
                    {sku.imageUrl ? (
                        <img src={sku.imageUrl} alt="" className="w-full h-full object-contain scale-125" loading="lazy"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }} />
                    ) : (
                        <Package className="h-4 w-4 text-muted-foreground" />
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">{stripBrandPrefixFromProductName(sku.name, sku.brand || brand)}</p>
                    <p className="module-product-subtitle">{sku.category}</p>
                </div>

                {/* Right side info */}
                <div className="flex items-center gap-2 flex-shrink-0">
                    {mode === 'listing' ? (
                        <>
                            {sku.issues.slice(0, 2).map((issue, idx) => (
                                <IssuePill key={idx} issue={issue} />
                            ))}
                            {sku.issues.length > 2 && (
                                <span className="text-xs font-semibold text-muted-foreground">+{sku.issues.length - 2}</span>
                            )}
                            <div className="flex items-center gap-1 text-xs font-semibold text-muted-foreground ml-1">
                                <MapPin className="h-3 w-3" />
                                {sku.affectedCities}
                            </div>
                        </>
                    ) : (
                        <>
                            <PSLPill level={sku.psl} />
                            <span className="text-xs font-bold text-muted-foreground">
                                OSA: <OSAValue value={sku.osa ?? 0} size="sm" />
                            </span>
                        </>
                    )}
                </div>
            </button>

            {/* Expanded: city breakdown */}
            {expanded && hasCities && (
                <div className="border-t border-black/[0.04] dark:border-white/[0.06] bg-muted/10 dark:bg-white/[0.02] animate-in fade-in slide-in-from-top-1 duration-200">
                    <div className="px-4 py-2 space-y-1">
                        <div className="grid grid-cols-[1fr_auto_auto] gap-2 text-xs text-muted-foreground font-bold uppercase tracking-wider px-2 pb-1">
                            <span>City</span>
                            <span className="w-24 text-right">Issue</span>
                            <span className="w-16 text-right">Stores</span>
                        </div>
                        {sku.cityWiseIssues.map((row, idx) => (
                            <div key={idx} className={cn("grid grid-cols-[1fr_auto_auto] gap-2 items-center px-2 py-2", GLASS_INNER)}>
                                <div className="flex items-center gap-2 min-w-0">
                                    <MapPin className="h-3 w-3 text-muted-foreground/60 flex-shrink-0" />
                                    <span className="text-xs font-semibold text-foreground truncate">{row.city}</span>
                                </div>
                                <div className="w-24 text-right">
                                    <IssuePill issue={row.issueType} />
                                </div>
                                <span className="w-16 text-right text-xs font-bold text-primary">{row.storeCount}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}

// ============================================================================
// CATEGORY CARD (Horizontal scroll — same pattern as CategoryMetricsCard)
// ============================================================================

function AvailCategoryCard({ category, isSelected, onSelect }: {
    category: CategoryAvailability
    isSelected: boolean
    onSelect: () => void
}) {
    const osa = category.avgOSA ?? 0

    return (
        <div
            onClick={onSelect}
            className={cn("group/card w-full cursor-pointer", GLASS_CARD, GLASS_HOVER, isSelected && GLASS_SELECTED)}
        >
            <div className="px-5 pt-5 pb-0 flex flex-col justify-between min-h-[150px]">
                <div>
                    <div className="flex items-center gap-2.5 mb-1.5">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <BarChart3 className="h-4 w-4 text-primary" />
                        </div>
                        <h3 className="module-card-title truncate">{category.name}</h3>
                    </div>
                    <div className="flex items-center gap-2.5 ml-[42px]">
                        <p className="module-card-subtitle font-medium">{category.skuCount} SKUs</p>
                        <PSLPill level={category.psl} />
                    </div>
                </div>

                <div className="grid grid-cols-3 gap-4 mt-5">
                    <div className="flex items-center gap-2">
                        <Activity className="h-4 w-4 text-muted-foreground/70 flex-shrink-0" />
                        <div>
                            <span className="module-metric-caption block mb-1.5">Avg OSA</span>
                            <span className="module-metric-value">{osa >= 80 ? <span className="text-emerald-700 dark:text-emerald-400">{osa.toFixed(0)}%</span> : osa >= 50 ? <span className="text-amber-700 dark:text-amber-400">{osa.toFixed(0)}%</span> : <span className="text-red-700 dark:text-red-400">{osa.toFixed(0)}%</span>}</span>
                        </div>
                    </div>
                    <div>
                        <span className="module-metric-caption block mb-1.5">Avg DOI</span>
                        <span className="module-metric-value">{getDummyDOI(category.name)}d</span>
                    </div>
                    <div>
                        <span className="module-metric-caption block mb-1.5">Critical</span>
                        <span className={cn("module-metric-value", (category.criticalIssues ?? 0) > 0 ? "text-red-700 dark:text-red-400" : "text-foreground")}>
                            {category.criticalIssues ?? 0}
                        </span>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className={cn(
                "group/footer relative overflow-hidden flex items-center justify-between px-5 py-3 mt-2",
                "border-t border-black/[0.04] dark:border-white/[0.06]",
                "text-xs font-semibold transition-colors duration-200 ease-out",
                "hover:bg-primary/[0.06] hover:border-primary/15",
                isSelected ? "text-primary" : "text-muted-foreground group-hover/card:text-primary/80"
            )}>
                <div className="absolute inset-0 bg-gradient-to-r from-primary/[0.04] via-primary/[0.06] to-transparent opacity-0 transition-opacity duration-200 group-hover/footer:opacity-100 pointer-events-none" />
                <span className={cn("relative transition-colors duration-200 group-hover/footer:text-primary", isSelected && "text-primary")}>
                    {isSelected ? 'Viewing cities' : 'View cities'}
                </span>
                <div className="relative flex items-center gap-0.5">
                    <ChevronRight className={cn("h-3.5 w-3.5 transition-colors duration-200 group-hover/footer:!text-primary", isSelected && "text-primary")} />
                    <ChevronRight className={cn("h-3.5 w-3.5 -ml-2 transition-colors duration-200 text-transparent group-hover/footer:!text-primary/40", isSelected && "text-primary/30")} />
                </div>
            </div>
        </div>
    )
}

// ============================================================================
// CITY CARD
// ============================================================================

function CityCard({ city, onDrilldown }: { city: CityAvailability; onDrilldown: () => void }) {
    const osa = city.osa ?? 0

    return (
        <div onClick={onDrilldown} className={cn("group/card relative overflow-hidden cursor-pointer", GLASS_CARD, GLASS_HOVER)}>
            <div className="relative z-[1]">
                <div className="p-3 pb-2">
                    <div className="flex gap-3 items-start">
                        <div className="w-11 h-11 rounded-lg flex-shrink-0 flex items-center justify-center bg-white/70 dark:bg-white/10 border border-white/60 dark:border-white/10">
                            <MapPin className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="module-product-title">{city.name}</h3>
                            <PSLPill level={city.psl} />
                        </div>
                    </div>
                </div>

                <div className={cn("mx-3", GLASS_DIVIDER)} />

                <div className="p-3 pt-2">
                    <div className="flex items-stretch gap-2">
                        <div className={cn("flex-1 p-2 text-center", GLASS_INNER)}>
                            <span className="module-metric-kicker block mb-1">OSA</span>
                            <OSAValue value={osa} />
                        </div>
                        <div className={cn("flex-1 p-2 text-center", GLASS_INNER)}>
                            <span className="module-metric-kicker block mb-1">DOI</span>
                            <span className="module-metric-value">{getDummyDOI(city.name)}d</span>
                        </div>
                    </div>

                    {city.impactedSKUs > 0 && (
                        <div className={cn("flex items-center justify-center gap-1 mt-2 py-1.5 rounded-lg text-xs font-semibold", "bg-amber-500/10 text-amber-700 dark:text-amber-400")}>
                            <AlertTriangle className="h-3.5 w-3.5" />
                            {city.impactedSKUs} impacted SKUs
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className={cn(
                    "group/footer mx-3 mb-2.5 mt-0.5 flex items-center justify-between px-2.5 py-1.5 rounded-xl relative overflow-hidden",
                    "bg-primary/[0.04] dark:bg-primary/[0.08] border border-primary/10 dark:border-primary/15",
                    "transition-colors duration-200 ease-out",
                    "group-hover/card:bg-primary/[0.07] group-hover/card:border-primary/20",
                    "hover:!bg-primary/[0.14] hover:!border-primary/30 hover:shadow-[0_0_12px_rgba(var(--primary-rgb,99,102,241),0.15)]",
                )}>
                    <div className="absolute inset-0 bg-gradient-to-r from-primary/[0.05] via-primary/[0.08] to-transparent opacity-0 transition-opacity duration-200 group-hover/footer:opacity-100 pointer-events-none" />
                    <span className={cn("relative text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 transition-colors duration-200 group-hover/card:text-primary/80 group-hover/footer:!text-primary")}>
                        Drilldown
                    </span>
                    <div className="relative flex items-center gap-0.5">
                        <ChevronRight className={cn("h-3.5 w-3.5 transition-colors duration-200 text-muted-foreground/50 group-hover/card:text-primary/60 group-hover/footer:!text-primary")} />
                        <ChevronRight className={cn("h-3.5 w-3.5 -ml-2 transition-colors duration-200 text-transparent group-hover/footer:!text-primary/40")} />
                    </div>
                </div>
            </div>
        </div>
    )
}

// ============================================================================
// SKELETON LOADERS (matches Visibility module pattern)
// ============================================================================

function ImpactedSKUCardSkeleton() {
    return (
        <div className={cn("flex-shrink-0 w-[320px]", GLASS_CARD)}>
            <div className="p-3.5 pb-2.5">
                <div className="flex gap-2.5 items-start">
                    <div className="w-10 h-10 rounded-lg bg-muted animate-pulse" />
                    <div className="flex-1 space-y-1.5">
                        <div className="h-3.5 w-28 bg-muted animate-pulse rounded" />
                        <div className="h-3 w-16 bg-muted animate-pulse rounded" />
                    </div>
                </div>
            </div>
            <div className={cn("mx-3.5", GLASS_DIVIDER)} />
            <div className="px-3.5 pt-2.5 pb-1.5">
                <div className="grid grid-cols-3 gap-2">
                    {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className={cn("p-2 rounded-lg flex flex-col items-center gap-1", GLASS_INNER)}>
                            <div className="h-2.5 w-8 bg-muted animate-pulse rounded" />
                            <div className="h-4 w-10 bg-muted animate-pulse rounded" />
                        </div>
                    ))}
                </div>
            </div>
            <div className="px-3.5 pb-3 pt-1.5 flex gap-1.5">
                <div className="h-5 w-18 bg-muted animate-pulse rounded-lg" />
                <div className="h-5 w-20 bg-muted animate-pulse rounded-lg" />
            </div>
        </div>
    )
}

function AvailCategoryCardSkeleton() {
    return (
        <div className={cn("w-[360px]", GLASS_CARD)}>
            <div className="px-5 pt-5 pb-0 min-h-[150px]">
                <div className="flex items-center gap-2.5 mb-1.5">
                    <div className="w-8 h-8 rounded-lg bg-muted animate-pulse" />
                    <div className="h-5 w-28 bg-muted animate-pulse rounded" />
                </div>
                <div className="flex items-center gap-2.5 ml-[42px] mb-5">
                    <div className="h-3 w-14 bg-muted animate-pulse rounded" />
                    <div className="h-5 w-16 bg-muted animate-pulse rounded-lg" />
                </div>
                <div className="grid grid-cols-3 gap-4 mt-5">
                    <div className="space-y-1.5">
                        <div className="h-3 w-12 bg-muted animate-pulse rounded" />
                        <div className="h-6 w-10 bg-muted animate-pulse rounded" />
                    </div>
                    <div className="space-y-1.5">
                        <div className="h-3 w-14 bg-muted animate-pulse rounded" />
                        <div className="h-6 w-10 bg-muted animate-pulse rounded" />
                    </div>
                    <div className="space-y-1.5">
                        <div className="h-3 w-12 bg-muted animate-pulse rounded" />
                        <div className="h-6 w-8 bg-muted animate-pulse rounded" />
                    </div>
                </div>
            </div>
            <div className="px-5 py-3 mt-2 border-t border-black/[0.04] dark:border-white/[0.06]">
                <div className="h-4 w-20 bg-muted animate-pulse rounded" />
            </div>
        </div>
    )
}

function CityCardSkeleton() {
    return (
        <div className={cn(GLASS_CARD)}>
            <div className="p-3 pb-2">
                <div className="flex gap-3 items-start">
                    <div className="w-11 h-11 rounded-lg bg-muted animate-pulse" />
                    <div className="flex-1 space-y-2">
                        <div className="h-4 w-24 bg-muted animate-pulse rounded" />
                        <div className="h-5 w-16 bg-muted animate-pulse rounded-lg" />
                    </div>
                </div>
            </div>
            <div className={cn("mx-3", GLASS_DIVIDER)} />
            <div className="p-3 pt-2">
                <div className="flex items-stretch gap-2">
                    <div className={cn("flex-1 p-2 text-center space-y-1.5", GLASS_INNER)}>
                        <div className="h-3 w-8 mx-auto bg-muted animate-pulse rounded" />
                        <div className="h-5 w-10 mx-auto bg-muted animate-pulse rounded" />
                    </div>
                    <div className={cn("flex-1 p-2 text-center space-y-1.5", GLASS_INNER)}>
                        <div className="h-3 w-8 mx-auto bg-muted animate-pulse rounded" />
                        <div className="h-5 w-10 mx-auto bg-muted animate-pulse rounded" />
                    </div>
                </div>
                <div className="h-7 w-full bg-muted animate-pulse rounded-lg mt-2" />
            </div>
            <div className="mx-3 mb-2.5 mt-0.5 px-2.5 py-1.5 rounded-xl bg-primary/[0.04] dark:bg-primary/[0.08] border border-primary/10 dark:border-primary/15">
                <div className="h-3.5 w-16 bg-muted animate-pulse rounded" />
            </div>
        </div>
    )
}

// ============================================================================
// LOADING / ERROR
// ============================================================================



function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
    return (
        <div className="flex flex-col items-center justify-center min-h-[400px] border border-dashed rounded-[2rem] bg-accent/20 text-center p-8">
            <AlertTriangle className="h-12 w-12 text-destructive mb-4" />
            <p className="text-muted-foreground mb-6 max-w-sm">{message}</p>
            <Button onClick={onRetry} variant="outline" className="rounded-xl">
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
            </Button>
        </div>
    )
}

// ============================================================================
// SKU DETAIL SIDEBAR — Opens when clicking an impacted SKU card
// ============================================================================

function SKUDetailSidebar({ isOpen, sku, brand, onClose, workspaceId, platform, dateRange }: {
    isOpen: boolean
    sku: SKUAvailability | null
    mode?: 'listing' | 'psl'
    brand: string
    onClose: () => void
    workspaceId?: string
    platform?: string
    dateRange?: AvailabilityFilters['dateRange']
}) {
    const [cityDetails, setCityDetails] = useState<CityAvailabilityDetail[]>([])
    const [loading, setLoading] = useState(false)
    const [expandedCities, setExpandedCities] = useState<Set<number>>(new Set())
    const [expandedClusters, setExpandedClusters] = useState<Set<string>>(new Set())
    const [expandedPincodes, setExpandedPincodes] = useState<Set<string>>(new Set())

    const utils = trpc.useUtils()

    // Fetch darkstore hierarchy when SKU changes
    useEffect(() => {
        if (!isOpen || !sku) {
            setCityDetails([])
            setExpandedCities(new Set())
            setExpandedClusters(new Set())
            setExpandedPincodes(new Set())
            return
        }

        let cancelled = false
        setLoading(true)

        utils.availability.skuFocus.fetch({
            skuId: sku.id,
            workspaceId,
            platform: platform !== 'all' ? platform : undefined,
            dateRange: dateRange,
        }).then((data) => {
            if (!cancelled && data) {
                setCityDetails(data.cityDetails)
            }
        }).catch((err) => {
            console.error('Failed to load SKU focus data:', err)
        }).finally(() => {
            if (!cancelled) setLoading(false)
        })

        return () => { cancelled = true }
    }, [isOpen, sku?.id, workspaceId, platform, dateRange])

    // Build the cluster/pincode hierarchy per city and recompute city-level
    // metrics from only the stores with valid pincodes (matching what children show)
    // NOTE: This useMemo MUST stay above the early return to satisfy React's rules of hooks.
    const cityHierarchy = useMemo(() => {
        const pincodeRegex = /^[0-9]{6}$/
        return cityDetails.map((city) => {
            const groups = new Map<string, {
                clusterId?: number
                clusterName: string
                stores: DarkStoreAvailability[]
                pincodeMap: Map<string, DarkStoreAvailability[]>
            }>()

            for (const ds of city.darkStores) {
                const normalizedPincode = ds.pincode?.trim() || ds.locality?.trim()
                if (!normalizedPincode || !pincodeRegex.test(normalizedPincode)) continue

                const key = ds.clusterName || 'All'
                if (!groups.has(key)) {
                    groups.set(key, {
                        clusterId: ds.clusterId,
                        clusterName: key,
                        stores: [],
                        pincodeMap: new Map(),
                    })
                }
                groups.get(key)!.stores.push(ds)
                if (!groups.get(key)!.pincodeMap.has(normalizedPincode)) {
                    groups.get(key)!.pincodeMap.set(normalizedPincode, [])
                }
                groups.get(key)!.pincodeMap.get(normalizedPincode)!.push(ds)
            }

            const clusterGroups = Array.from(groups.values())
                .map((group) => ({
                    ...group,
                    pincodeGroups: Array.from(group.pincodeMap.entries())
                        .map(([pincode, stores]) => ({ pincode, stores }))
                        .sort((a, b) => a.pincode.localeCompare(b.pincode)),
                }))
                .sort((a, b) => a.clusterName.localeCompare(b.clusterName))

            // Recompute city-level metrics from stores that actually appear in the hierarchy
            const filteredStores = clusterGroups.flatMap((c) => c.stores)
            const count = filteredStores.length
            const computedWOsa = count > 0 ? filteredStores.reduce((a, s) => a + (s.weightedOsa ?? 0), 0) / count : 0
            const computedAvgOsa = count > 0 ? filteredStores.reduce((a, s) => a + (s.avgOsa ?? 0), 0) / count : 0

            return {
                ...city,
                clusterGroups,
                storeCount: count,
                computedWOsa,
                computedAvgOsa,
            }
        })
    }, [cityDetails])

    if (!isOpen || !sku) return null

    const displayName = stripBrandPrefixFromProductName(sku.name, sku.brand || brand)
    const osa = sku.osa ?? 0

    const toggleCity = (cityId: number) => {
        setExpandedCities((prev) => {
            const next = new Set(prev)
            if (next.has(cityId)) next.delete(cityId); else next.add(cityId)
            return next
        })
    }
    const toggleCluster = (key: string) => {
        setExpandedClusters((prev) => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key); else next.add(key)
            return next
        })
    }
    const togglePincode = (key: string) => {
        setExpandedPincodes((prev) => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key); else next.add(key)
            return next
        })
    }

    return createPortal(
        <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/80 animate-in fade-in duration-300" onClick={onClose} />
            <div className="absolute right-0 top-0 h-full w-full lg:w-[65vw] max-w-4xl bg-background shadow-2xl border-l border-border/40 flex flex-col animate-in slide-in-from-right duration-400 ease-out sm:rounded-l-3xl overflow-hidden">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-6 sm:px-10 py-6 sm:py-8 border-b border-border/40 bg-card/50 backdrop-blur-xl flex-shrink-0 z-10">
                    <div className="flex items-center gap-5 flex-1 min-w-0">
                        <div className="relative">
                            <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full" />
                            {sku.imageUrl ? (
                                <img src={sku.imageUrl} alt={displayName}
                                    className="relative w-20 h-20 sm:w-24 sm:h-24 object-contain bg-white rounded-2xl border border-border/50 p-2 shadow-lg flex-shrink-0" />
                            ) : (
                                <div className="relative w-20 h-20 sm:w-24 sm:h-24 bg-primary/10 rounded-2xl border border-primary/20 flex items-center justify-center shadow-lg">
                                    <Package className="w-10 h-10 text-primary" />
                                </div>
                            )}
                        </div>
                        <div className="min-w-0 flex-1">
                            <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider rounded-md mb-2">
                                {sku.category}
                            </span>
                            <h3 className="text-2xl sm:text-3xl font-bold text-foreground leading-tight truncate">{displayName}</h3>
                            <p className="text-base font-semibold text-muted-foreground mt-1 flex items-center gap-2">
                                <span>{sku.brand || brand}</span>
                                <span className="w-1.5 h-1.5 rounded-full bg-border" />
                                <span>City / Cluster / Pincode / Darkstore</span>
                            </p>
                        </div>
                    </div>
                    <Button variant="secondary" size="icon"
                        className="rounded-xl hover:bg-destructive hover:text-destructive-foreground transition-colors h-9 w-9 flex-shrink-0 shadow-sm"
                        onClick={onClose}>
                        <X className="h-4 w-4" />
                    </Button>
                </div>

                {/* Summary stats bar */}
                <div className="flex items-center gap-4 px-6 sm:px-10 py-4 border-b border-border/40 bg-muted/20 flex-wrap">
                    <div className={cn("flex items-center gap-2 px-4 py-2 rounded-xl", GLASS_INNER)}>
                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">PSL</span>
                        <PSLPill level={sku.psl} />
                    </div>
                    <div className={cn("flex items-center gap-2 px-4 py-2 rounded-xl", GLASS_INNER)}>
                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">OSA</span>
                        <OSAValue value={osa} size="sm" />
                    </div>
                    <div className={cn("flex items-center gap-2 px-4 py-2 rounded-xl", GLASS_INNER)}>
                        <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-sm font-bold text-foreground">{loading ? '...' : cityDetails.length} cities</span>
                    </div>
                    <div className={cn("flex items-center gap-2 px-4 py-2 rounded-xl", GLASS_INNER)}>
                        <Store className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-sm font-bold text-foreground">{sku.affectedStores} stores</span>
                    </div>
                    {sku.listingIssues > 0 && (
                        <div className={cn("flex items-center gap-2 px-4 py-2 rounded-xl", GLASS_INNER)}>
                            <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                            <span className="text-sm font-bold text-foreground">{sku.listingIssues} issues</span>
                        </div>
                    )}
                </div>

                {/* Hierarchical City > Cluster > Pincode > Darkstore table */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-8">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-16 gap-4">
                            <Loader2 className="h-8 w-8 text-primary animate-spin" />
                            <p className="text-sm text-muted-foreground font-medium">Loading availability data...</p>
                        </div>
                    ) : cityHierarchy.length > 0 ? (
                        <div className="space-y-1">
                            {/* Table header */}
                            <div className="grid grid-cols-[1fr_auto_auto_auto] gap-2 px-4 py-2 text-xs text-muted-foreground font-bold uppercase tracking-wider border-b border-border">
                                <span>City / Cluster / Pincode / Darkstore</span>
                                <span className="w-16 text-center">PSL</span>
                                <span className="w-20 text-right">WL OSA %</span>
                                <span className="w-20 text-right">Avg OSA %</span>
                            </div>

                            {cityHierarchy.map((city) => {
                                const cityExpanded = expandedCities.has(city.cityId)
                                const cityPsl: PSLLevel | undefined = city.computedWOsa >= 85 ? 'Low' : city.computedWOsa >= 70 ? 'Medium' : 'High'

                                return (
                                    <div key={city.cityId} className="border-b border-border/50 last:border-0">
                                        {/* City row */}
                                        <button
                                            onClick={() => toggleCity(city.cityId)}
                                            className="w-full grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center px-3 py-3 hover:bg-muted/30 transition-colors text-left"
                                        >
                                            <div className="flex items-center gap-3 min-w-0">
                                                <ChevronRight className={cn("h-4 w-4 text-muted-foreground flex-shrink-0 transition-transform", cityExpanded && "rotate-90")} />
                                                <div className="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center bg-white/70 dark:bg-white/10 border border-white/60 dark:border-white/10">
                                                    <MapPin className="h-4 w-4 text-primary" />
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="text-sm font-semibold text-foreground truncate">{city.cityName}</p>
                                                    <span className="text-xs text-muted-foreground">{city.storeCount} stores</span>
                                                </div>
                                            </div>
                                            <span className="w-16 text-center"><PSLPill level={cityPsl} /></span>
                                            <span className="w-20 text-right text-sm font-semibold text-primary">{city.computedWOsa.toFixed(1)}%</span>
                                            <span className="w-20 text-right text-sm">{city.computedAvgOsa.toFixed(1)}%</span>
                                        </button>

                                        {/* Cluster rows */}
                                        {cityExpanded && city.clusterGroups.length > 0 && (
                                            <div className="bg-muted/20 border-t border-border/50">
                                                {city.clusterGroups.map((cluster) => {
                                                    const clusterKey = `${city.cityId}:${cluster.clusterName}`
                                                    const clusterExpanded = expandedClusters.has(clusterKey)
                                                    const cAvg = cluster.stores.reduce((a, s) => a + (s.avgOsa ?? 0), 0) / (cluster.stores.length || 1)
                                                    const cW = cluster.stores.reduce((a, s) => a + (s.weightedOsa ?? 0), 0) / (cluster.stores.length || 1)

                                                    return (
                                                        <div key={clusterKey}>
                                                            <button
                                                                onClick={() => toggleCluster(clusterKey)}
                                                                className="w-full grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center pl-10 pr-3 py-2.5 hover:bg-muted/30 transition-colors text-left border-b border-border/30"
                                                            >
                                                                <div className="flex items-center gap-2 min-w-0">
                                                                    <ChevronRight className={cn("h-3.5 w-3.5 text-muted-foreground flex-shrink-0 transition-transform", clusterExpanded && "rotate-90")} />
                                                                    <Store className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                                                    <span className="text-sm font-medium text-foreground truncate">{cluster.clusterName}</span>
                                                                    <span className="text-xs text-muted-foreground flex-shrink-0">({cluster.pincodeGroups.length} pins)</span>
                                                                </div>
                                                                <span className="w-16 text-center" />
                                                                <span className="w-20 text-right text-sm text-primary font-medium">{cW.toFixed(1)}%</span>
                                                                <span className="w-20 text-right text-sm">{cAvg.toFixed(1)}%</span>
                                                            </button>

                                                            {/* Pincode rows */}
                                                            {clusterExpanded && cluster.pincodeGroups.map((pincodeGroup) => {
                                                                const pincodeKey = `${clusterKey}:${pincodeGroup.pincode}`
                                                                const pincodeExpanded = expandedPincodes.has(pincodeKey)
                                                                const pAvg = pincodeGroup.stores.reduce((a, s) => a + (s.avgOsa ?? 0), 0) / (pincodeGroup.stores.length || 1)
                                                                const pW = pincodeGroup.stores.reduce((a, s) => a + (s.weightedOsa ?? 0), 0) / (pincodeGroup.stores.length || 1)

                                                                return (
                                                                    <div key={pincodeKey}>
                                                                        <button
                                                                            onClick={() => togglePincode(pincodeKey)}
                                                                            className="w-full grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center pl-16 pr-3 py-2 hover:bg-muted/30 transition-colors text-left border-b border-border/30"
                                                                        >
                                                                            <div className="flex items-center gap-2 min-w-0">
                                                                                <ChevronRight className={cn("h-3.5 w-3.5 text-muted-foreground flex-shrink-0 transition-transform", pincodeExpanded && "rotate-90")} />
                                                                                <MapPin className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                                                                <span className="text-sm font-medium text-foreground truncate">{pincodeGroup.pincode}</span>
                                                                                <span className="text-xs text-muted-foreground flex-shrink-0">({pincodeGroup.stores.length})</span>
                                                                            </div>
                                                                            <span className="w-16 text-center" />
                                                                            <span className="w-20 text-right text-sm text-primary font-medium">{pW.toFixed(1)}%</span>
                                                                            <span className="w-20 text-right text-sm">{pAvg.toFixed(1)}%</span>
                                                                        </button>

                                                                        {/* Darkstore rows */}
                                                                        {pincodeExpanded && pincodeGroup.stores.map((ds, idx) => (
                                                                            <div key={idx} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center pl-24 pr-3 py-2 border-b border-border/20 last:border-0 text-sm">
                                                                                <span className="text-foreground truncate">{ds.storeName || ds.locality || ds.storeCode}</span>
                                                                                <span className="w-16 text-center"><PSLPill level={ds.psl} /></span>
                                                                                <span className="w-20 text-right text-primary font-medium">{(ds.weightedOsa ?? 0).toFixed(1)}%</span>
                                                                                <span className="w-20 text-right">{(ds.avgOsa ?? 0).toFixed(1)}%</span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )
                                                            })}
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-16 text-center">
                            <MapPin className="h-12 w-12 text-muted-foreground/30 mb-4" />
                            <p className="text-muted-foreground font-medium text-lg">No city-level data available for this SKU</p>
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    )
}

// ============================================================================
// CITY DRILLDOWN SIDEBAR
// ============================================================================

function DrilldownSidebar({ isOpen, isLoading, data, city, categoryName, viewBy, onViewByChange, onClose, brand }: {
    isOpen: boolean; isLoading: boolean; data: CityFocusResponse | null; city: CityAvailability | null
    categoryName: string; viewBy: 'osa' | 'doi'; onViewByChange: (v: 'osa' | 'doi') => void; onClose: () => void; brand: string
}) {
    if (!isOpen) return null

    return createPortal(
        <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/80 animate-in fade-in duration-300" onClick={onClose} />
            <div className="absolute right-0 top-0 h-full w-full lg:w-[75vw] max-w-6xl bg-background shadow-2xl border-l border-border/40 flex flex-col animate-in slide-in-from-right duration-400 ease-out sm:rounded-l-3xl overflow-hidden">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-6 sm:px-10 py-6 sm:py-8 border-b border-border/40 bg-card/50 backdrop-blur-xl flex-shrink-0 z-10">
                    <div className="flex items-center gap-5 flex-1 min-w-0">
                        <div className="relative">
                            <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full" />
                            <div className="relative w-20 h-20 sm:w-24 sm:h-24 bg-primary/10 rounded-2xl border border-primary/20 flex items-center justify-center shadow-lg">
                                <MapPin className="w-10 h-10 text-primary" />
                            </div>
                        </div>
                        <div className="min-w-0 flex-1">
                            <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider rounded-md mb-2">{categoryName}</span>
                            <h3 className="text-2xl sm:text-3xl font-bold text-foreground leading-tight truncate">{city?.name || 'City'}</h3>
                            <p className="text-base font-semibold text-muted-foreground mt-1">Darkstore Availability</p>
                        </div>
                    </div>
                    <Button variant="secondary" size="icon" className="rounded-xl hover:bg-destructive hover:text-destructive-foreground transition-colors h-9 w-9 flex-shrink-0 shadow-sm" onClick={onClose}>
                        <X className="h-4 w-4" />
                    </Button>
                </div>

                {/* Tabs */}
                <div className="px-6 sm:px-10 py-4 border-b border-border/40">
                    <div className="flex gap-1 p-1 bg-muted rounded-lg w-fit">
                        {(['osa', 'doi'] as const).map((tab) => (
                            <button key={tab} onClick={() => onViewByChange(tab)}
                                className={cn("px-4 py-2 text-sm font-medium rounded-md transition-colors",
                                    viewBy === tab
                                        ? "bg-background text-foreground shadow-sm"
                                        : "text-muted-foreground hover:text-foreground")}>
                                {tab.toUpperCase()}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-8 relative">
                    {isLoading && (
                        <div className="absolute inset-0 bg-background/30 backdrop-blur-[2px] z-30 flex items-center justify-center">
                            <div className="flex items-center gap-3 bg-muted/80 border border-border/40 rounded-xl px-5 py-3 shadow-sm">
                                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                <span className="text-sm text-muted-foreground font-medium">Refreshing data...</span>
                            </div>
                        </div>
                    )}
                    {!isLoading && data && (
                        <div className="space-y-1">
                            <div className="grid grid-cols-[1fr_auto_auto] gap-3 px-3 py-2 text-xs text-muted-foreground font-medium border-b border-border">
                                <span>SKU</span>
                                <span className="w-24 text-right">{viewBy === 'osa' ? 'Weighted OSA' : 'DOI'}</span>
                                <span className="w-20 text-right">DS Listing %</span>
                            </div>
                            {data.skus.map((sku) => <DrilldownSKURow key={sku.id} sku={sku} viewBy={viewBy} brand={brand} />)}
                            {data.skus.length === 0 && <p className="text-center text-muted-foreground py-8">No SKUs found</p>}
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    )
}

function DrilldownSKURow({ sku, viewBy, brand }: { sku: SKUAvailability; viewBy: 'osa' | 'doi'; brand: string }) {
    const [expanded, setExpanded] = useState(false)
    const [expandedClusters, setExpandedClusters] = useState<Set<string>>(new Set())
    const [expandedPincodes, setExpandedPincodes] = useState<Set<string>>(new Set())
    const darkStores = sku.cityAvailabilityDetails?.[0]?.darkStores || []

    const clusterGroups = useMemo(() => {
        const pincodeRegex = /^[0-9]{6}$/
        const groups = new Map<string, {
            clusterId?: number
            clusterName: string
            stores: typeof darkStores
            pincodeMap: Map<string, typeof darkStores>
        }>()
        for (const ds of darkStores) {
            const normalizedPincode = ds.pincode?.trim() || ds.locality?.trim()
            if (!normalizedPincode || !pincodeRegex.test(normalizedPincode)) continue

            const key = ds.clusterName || 'All'
            if (!groups.has(key)) {
                groups.set(key, {
                    clusterId: ds.clusterId,
                    clusterName: key,
                    stores: [],
                    pincodeMap: new Map(),
                })
            }
            groups.get(key)!.stores.push(ds)
            if (!groups.get(key)!.pincodeMap.has(normalizedPincode)) {
                groups.get(key)!.pincodeMap.set(normalizedPincode, [])
            }
            groups.get(key)!.pincodeMap.get(normalizedPincode)!.push(ds)
        }
        return Array.from(groups.values())
            .map((group) => ({
                ...group,
                pincodeGroups: Array.from(group.pincodeMap.entries())
                    .map(([pincode, stores]) => ({ pincode, stores }))
                    .sort((a, b) => a.pincode.localeCompare(b.pincode)),
            }))
            .sort((a, b) => a.clusterName.localeCompare(b.clusterName))
    }, [darkStores])

    // Compute city-specific OSA from the darkstores that actually appear in the hierarchy,
    // instead of using sku.weightedOsa which is a cross-city aggregate
    const citySpecificMetrics = useMemo(() => {
        const allStores = clusterGroups.flatMap((c) => c.stores)
        const count = allStores.length
        if (count === 0) {
            return { weightedOsa: sku.weightedOsa ?? sku.osa ?? 0, listingPct: sku.listingPercentage ?? 100 }
        }
        const wOsa = allStores.reduce((a, s) => a + (s.weightedOsa ?? 0), 0) / count
        const listing = allStores.reduce((a, s) => a + (s.listingPercentage ?? (s.isAvailable ? 100 : 0)), 0) / count
        return { weightedOsa: wOsa, listingPct: listing }
    }, [clusterGroups, sku.weightedOsa, sku.osa, sku.listingPercentage])

    const toggleCluster = (clusterName: string) => {
        setExpandedClusters((prev) => {
            const next = new Set(prev)
            if (next.has(clusterName)) next.delete(clusterName); else next.add(clusterName)
            return next
        })
    }
    const togglePincode = (clusterName: string, pincode: string) => {
        const key = `${clusterName}:${pincode}`
        setExpandedPincodes((prev) => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key); else next.add(key)
            return next
        })
    }

    return (
        <div className="border-b border-border last:border-0">
            <button onClick={() => setExpanded(!expanded)}
                className="w-full grid grid-cols-[1fr_auto_auto] gap-3 items-center px-3 py-3 hover:bg-muted/30 transition-colors text-left">
                <div className="flex items-center gap-3 min-w-0">
                    <ChevronRight className={cn("h-4 w-4 text-muted-foreground flex-shrink-0 transition-transform", expanded && "rotate-90")} />
                    <div className="w-8 h-8 rounded overflow-hidden bg-muted flex-shrink-0">
                        {sku.imageUrl ? <img src={sku.imageUrl} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-muted" />}
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{stripBrandPrefixFromProductName(sku.name, sku.brand || brand)}</p>
                        <PSLPill level={sku.psl} />
                    </div>
                </div>
                <span className="w-24 text-right text-sm font-semibold">
                    {viewBy === 'osa' ? `${citySpecificMetrics.weightedOsa.toFixed(1)}%` : `${getDummyDOI(sku.id)}d`}
                </span>
                <span className="w-20 text-right text-sm">{citySpecificMetrics.listingPct.toFixed(1)}%</span>
            </button>

            {expanded && clusterGroups.length > 0 && (
                <div className="bg-muted/20 border-t border-border">
                    <div className="px-4 py-2">
                        <div className="grid grid-cols-[1fr_auto_auto_auto] gap-2 text-xs text-muted-foreground font-medium mb-1 px-2">
                            <span>Cluster / Pincode / Darkstore</span>
                            <span className="w-16 text-center">PSL</span>
                            <span className="w-20 text-right">WL OSA %</span>
                            <span className="w-20 text-right">Avg OSA %</span>
                        </div>
                        {clusterGroups.map((cluster) => {
                            const ce = expandedClusters.has(cluster.clusterName)
                            const cAvg = cluster.stores.reduce((a, s) => a + (s.avgOsa ?? 0), 0) / (cluster.stores.length || 1)
                            const cW = cluster.stores.reduce((a, s) => a + (s.weightedOsa ?? 0), 0) / (cluster.stores.length || 1)
                            return (
                                <div key={cluster.clusterName}>
                                    <button onClick={() => toggleCluster(cluster.clusterName)}
                                        className="w-full grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center px-2 py-2.5 hover:bg-muted/30 transition-colors text-left border-b border-border/50">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <ChevronRight className={cn("h-3.5 w-3.5 text-muted-foreground flex-shrink-0 transition-transform", ce && "rotate-90")} />
                                            <Store className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                            <span className="text-sm font-medium text-foreground truncate">{cluster.clusterName}</span>
                                            <span className="text-xs text-muted-foreground flex-shrink-0">({cluster.pincodeGroups.length} pins)</span>
                                        </div>
                                        <span className="w-16 text-center" />
                                        <span className="w-20 text-right text-sm text-primary font-medium">{cW.toFixed(1)}%</span>
                                        <span className="w-20 text-right text-sm">{cAvg.toFixed(1)}%</span>
                                    </button>
                                    {ce && cluster.pincodeGroups.map((pincodeGroup) => {
                                        const pincodeKey = `${cluster.clusterName}:${pincodeGroup.pincode}`
                                        const pe = expandedPincodes.has(pincodeKey)
                                        const pAvg = pincodeGroup.stores.reduce((a, s) => a + (s.avgOsa ?? 0), 0) / (pincodeGroup.stores.length || 1)
                                        const pW = pincodeGroup.stores.reduce((a, s) => a + (s.weightedOsa ?? 0), 0) / (pincodeGroup.stores.length || 1)
                                        return (
                                            <div key={pincodeKey}>
                                                <button onClick={() => togglePincode(cluster.clusterName, pincodeGroup.pincode)}
                                                    className="w-full grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center pl-10 pr-2 py-2 hover:bg-muted/30 transition-colors text-left border-b border-border/50">
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        <ChevronRight className={cn("h-3.5 w-3.5 text-muted-foreground flex-shrink-0 transition-transform", pe && "rotate-90")} />
                                                        <MapPin className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                                        <span className="text-sm font-medium text-foreground truncate">{pincodeGroup.pincode}</span>
                                                        <span className="text-xs text-muted-foreground flex-shrink-0">({pincodeGroup.stores.length})</span>
                                                    </div>
                                                    <span className="w-16 text-center" />
                                                    <span className="w-20 text-right text-sm text-primary font-medium">{pW.toFixed(1)}%</span>
                                                    <span className="w-20 text-right text-sm">{pAvg.toFixed(1)}%</span>
                                                </button>
                                                {pe && pincodeGroup.stores.map((ds, idx) => (
                                                    <div key={idx} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center pl-16 pr-2 py-2 border-b border-border/30 last:border-0 text-sm">
                                                        <span className="text-foreground truncate">{ds.storeName || ds.locality || ds.storeCode}</span>
                                                        <span className="w-16 text-center"><PSLPill level={ds.psl} /></span>
                                                        <span className="w-20 text-right text-primary font-medium">{(ds.weightedOsa ?? 0).toFixed(1)}%</span>
                                                        <span className="w-20 text-right">{(ds.avgOsa ?? 0).toFixed(1)}%</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )
                                    })}
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}
        </div>
    )
}

// ============================================================================
// MAIN PAGE
// ============================================================================

export function AvailabilityPage() {
    const {
        overview, categories, overviewCities, citiesWithClusters, citiesLoading, locationSelection,
        isLoading, isRefetching, error, filters, viewBy, selectedOverviewCategory,
        isSidebarOpen, sidebarData, sidebarLoading, sidebarCity,
        setFilters, setViewBy, setLocationSelection, selectCategory, openCityDrilldown, closeDrilldown, refresh,
    } = useAvailability()

    const { userBrand } = useSettings()
    const { setFilterContent } = useTopBar()
    const { currentWorkspace } = useWorkspace()

    const [activeTab, setActiveTab] = useState<'listing' | 'psl'>('listing')
    const [isImpactedExpanded, setIsImpactedExpanded] = useState(false)
    const [selectedSKU, setSelectedSKU] = useState<SKUAvailability | null>(null)

    // Filter SKUs by brand (auto-show all if no brand matches) and selected category
    const filteredOverview = useMemo(() => {
        if (!overview) return null
        const brandLower = userBrand.toLowerCase()
        const hasBrand = overview.topImpactedSKUs.some(sku =>
            (sku.brand && sku.brand.toLowerCase() === brandLower) || sku.name.toLowerCase().includes(brandLower)
        )
        let skus = hasBrand
            ? overview.topImpactedSKUs.filter(sku => {
                if (sku.brand) return sku.brand.toLowerCase() === brandLower
                return sku.name.toLowerCase().includes(brandLower)
            })
            : overview.topImpactedSKUs

        // Client-side category filter: when a category card is selected,
        // ensure only SKUs from that category are shown (covers store-price
        // SKUs that the backend may not filter by category)
        if (selectedOverviewCategory) {
            const catLower = selectedOverviewCategory.toLowerCase()
            skus = skus.filter(sku => (sku.category || '').toLowerCase() === catLower)
        }

        // Filter pending SKUs by brand similarly
        let pendingSKUs = overview.pendingSKUs ?? []
        const hasPendingBrand = pendingSKUs.some(sku =>
            (sku.brand && sku.brand.toLowerCase() === brandLower) || sku.name.toLowerCase().includes(brandLower)
        )
        let filteredPending = hasPendingBrand
            ? pendingSKUs.filter(sku => {
                if (sku.brand) return sku.brand.toLowerCase() === brandLower
                return sku.name.toLowerCase().includes(brandLower)
            })
            : pendingSKUs

        // Also filter pending SKUs by selected category
        if (selectedOverviewCategory) {
            const catLower = selectedOverviewCategory.toLowerCase()
            filteredPending = filteredPending.filter(sku => (sku.category || '').toLowerCase() === catLower)
        }

        return { ...overview, topImpactedSKUs: skus, pendingSKUs: filteredPending }
    }, [overview, userBrand, selectedOverviewCategory])

    const handlePlatformChange = useCallback((value: string) => {
        setFilters(prev => ({ ...prev, platform: value as AvailabilityFilters['platform'] }))
    }, [setFilters])
    
    const handleCategoryFilterChange = useCallback((value: string) => {
        selectCategory(value === 'All Categories' ? null : value)
    }, [selectCategory])
    
    const handleSkuFilterChange = useCallback((value: string) => {
        setFilters(prev => ({ ...prev, skuFilter: value as AvailabilityFilters['skuFilter'] }))
    }, [setFilters])

    const platformValue = filters.platform || 'all'
    const categoryOptions = useMemo(
        () => categories.map((category) => ({ label: category, value: category })),
        [categories]
    )

    // Convert filters.dateRange to DateRange for DateRangePicker
    const dateRangeValue: DateRange | undefined = useMemo(() => {
        if (!filters.dateRange || typeof filters.dateRange === 'string') return undefined
        return {
            from: new Date(filters.dateRange.startDate),
            to: new Date(filters.dateRange.endDate),
        }
    }, [filters.dateRange])

    // Handle date range change from DateRangePicker
    // Uses functional update to avoid infinite loop (don't include `filters` in deps)
    const handleDateRangeChange = useCallback((range: DateRange | undefined) => {
        if (range?.from && range?.to) {
            const customRange: CustomDateRange = {
                startDate: format(range.from, 'yyyy-MM-dd'),
                endDate: format(range.to, 'yyyy-MM-dd'),
            }
            setFilters(prev => ({ ...prev, dateRange: customRange }))
        }
    }, [setFilters])

    // Extract specific filter values to avoid re-running effect when unrelated filter properties change
    const categoryValue = filters.category || 'All Categories'
    const skuFilterValue = filters.skuFilter || 'all'

    // TopBar filters - only depend on values, not callbacks (callbacks are stable via useCallback)
    useEffect(() => {
        setFilterContent(
            <div className="flex items-center gap-2">
                <TopBarFilterDropdown value={platformValue} options={platformOptions} onChange={handlePlatformChange} />
                <div className="w-px h-4 bg-border/60" />
                <DateRangePicker
                    value={dateRangeValue}
                    onChange={handleDateRangeChange}
                />
                <div className="w-px h-4 bg-border/60" />
                <TopBarFilterDropdown value={categoryValue} options={categoryOptions} onChange={handleCategoryFilterChange} />
                <div className="w-px h-4 bg-border/60" />
                <TopBarFilterDropdown value={skuFilterValue} options={skuOptions} onChange={handleSkuFilterChange} />
                <div className="w-px h-4 bg-border/60" />
                <MultiSelectCityFilter selection={locationSelection} citiesWithClusters={citiesWithClusters} onChange={setLocationSelection} loading={citiesLoading} />
            </div>
        )
        return () => setFilterContent(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [platformValue, categoryValue, skuFilterValue, categoryOptions, locationSelection, citiesWithClusters, citiesLoading, dateRangeValue])

    // Visible impacted SKUs (limited to strip)
    const visibleImpacted = filteredOverview?.topImpactedSKUs.slice(0, MAX_VISIBLE_IMPACTED) ?? []
    const totalImpacted = filteredOverview?.topImpactedSKUs.length ?? 0
    const hasMoreImpacted = totalImpacted > MAX_VISIBLE_IMPACTED

    // Pending SKUs (tracked but no data yet)
    const pendingSKUs = filteredOverview?.pendingSKUs ?? []

    return (
        <>
            <div className="min-h-screen bg-[#FDFDFD] dark:bg-[#0A0A0A] text-foreground font-sans relative overflow-x-hidden">
                {/* Background */}
                <div className="absolute top-0 inset-x-0 h-96 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none z-0" />


                <div className="max-w-[1600px] mx-auto p-4 md:p-6 relative z-10 flex flex-col gap-6">

                    {error && <ErrorState message={error} onRetry={refresh} />}

                    {!error && (
                        <>
                            {/* ========================================== */}
                            {/* CATEGORIES — HORIZONTAL SCROLL             */}
                            {/* ========================================== */}
                            <section>
                                <div className="flex items-center gap-2 mb-4 px-1">
                                    <h3 className="module-section-title">Categories</h3>
                                    {filteredOverview ? (
                                        <span className="module-section-count">{filteredOverview.categories.length}</span>
                                    ) : (
                                        <span className="h-5 w-8 bg-muted animate-pulse rounded-md" />
                                    )}
                                </div>

                                {isLoading && !filteredOverview ? (
                                    <div className="flex gap-4 py-5 px-1">
                                        {Array.from({ length: 4 }).map((_, i) => (
                                            <div key={i} className="flex-shrink-0">
                                                <AvailCategoryCardSkeleton />
                                            </div>
                                        ))}
                                    </div>
                                ) : filteredOverview && filteredOverview.categories.length > 0 ? (
                                    <div className={cn("transition-opacity duration-300", isRefetching && "opacity-50 grayscale-[20%] pointer-events-none")}>
                                        <SliderCarousel className="gap-4 py-5 px-1">
                                            {filteredOverview.categories.map((cat) => (
                                                <div key={cat.id} className="flex-shrink-0 w-[360px]">
                                                    <AvailCategoryCard
                                                        category={cat}
                                                        isSelected={cat.name === 'All Categories' ? !selectedOverviewCategory : selectedOverviewCategory === cat.name}
                                                        onSelect={() => selectCategory(cat.name === 'All Categories' ? null : cat.name)}
                                                    />
                                                </div>
                                            ))}
                                        </SliderCarousel>
                                    </div>
                                ) : (
                                    <div className="flex gap-4 py-5 px-1">
                                        {Array.from({ length: 4 }).map((_, i) => (
                                            <div key={i} className={cn("flex-shrink-0 w-[360px] min-h-[210px] flex flex-col items-center justify-center gap-2", GLASS_CARD, "opacity-60")}>
                                                <BarChart3 className="h-8 w-8 text-muted-foreground/30" />
                                                <span className="text-sm font-medium text-muted-foreground">No categories found</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </section>

                            {/* ========================================== */}
                            {/* TOP IMPACTED SKUs — HORIZONTAL STRIP       */}
                            {/* ========================================== */}
                            <section>
                                <div className="flex items-center justify-between mb-4 px-1">
                                    <div className="flex items-center gap-2">
                                        <h3 className="module-section-title">
                                            Top Impacted SKUs
                                            {selectedOverviewCategory && (
                                                <span className="normal-case tracking-normal font-semibold text-primary ml-1">
                                                    in {selectedOverviewCategory}
                                                </span>
                                            )}
                                        </h3>
                                        {filteredOverview ? (
                                            <span className="module-section-count">{totalImpacted}</span>
                                        ) : (
                                            <span className="h-5 w-8 bg-muted animate-pulse rounded-md" />
                                        )}
                                        {pendingSKUs.length > 0 && (
                                            <span className="text-xs font-bold bg-amber-500/10 text-amber-700 dark:text-amber-400 px-2 py-1 rounded-md flex items-center gap-1">
                                                <Clock className="h-3 w-3" />
                                                {pendingSKUs.length} pending
                                            </span>
                                        )}
                                    </div>

                                    {/* Tab toggle */}
                                    <div className="flex items-center gap-2">
                                        <div className="flex rounded-xl p-0.5 bg-white/50 dark:bg-white/[0.08] backdrop-blur-sm border border-white/50 dark:border-white/[0.12] shadow-sm">
                                            <button onClick={() => setActiveTab('listing')}
                                                className={cn("flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-lg transition-colors duration-150",
                                                    activeTab === 'listing' ? "bg-red-500 text-white shadow-md shadow-red-500/20" : "text-muted-foreground hover:text-foreground")}>
                                                <EyeOff className="h-3 w-3" /> Listing
                                            </button>
                                            <button onClick={() => setActiveTab('psl')}
                                                className={cn("flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-lg transition-colors duration-150",
                                                    activeTab === 'psl' ? "bg-primary text-primary-foreground shadow-md shadow-primary/20" : "text-muted-foreground hover:text-foreground")}>
                                                <TrendingDown className="h-3 w-3" /> PSL
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {isLoading && !filteredOverview ? (
                                    <div className="flex gap-4 py-4 px-1">
                                        {Array.from({ length: 4 }).map((_, i) => (
                                            <ImpactedSKUCardSkeleton key={i} />
                                        ))}
                                    </div>
                                ) : (visibleImpacted.length > 0 || pendingSKUs.length > 0) ? (
                                    <div className={cn("transition-opacity duration-300", isRefetching && "opacity-50 grayscale-[20%] pointer-events-none")}>
                                        <SliderCarousel className="gap-4 py-4 px-1">
                                            {visibleImpacted.map((sku) => (
                                                <div key={sku.id} className="flex-shrink-0">
                                                    <ImpactedSKUCompactCard
                                                        sku={sku}
                                                        mode={activeTab}
                                                        brand={userBrand}
                                                        onClick={() => setSelectedSKU(sku)}
                                                    />
                                                </div>
                                            ))}

                                            {/* Show More / Show Less toggle */}
                                            {hasMoreImpacted && !isImpactedExpanded && (
                                                <div
                                                    onClick={() => setIsImpactedExpanded(true)}
                                                    className={cn(
                                                        "flex-shrink-0 w-[140px] self-stretch cursor-pointer flex flex-col items-center justify-center gap-3",
                                                        GLASS_CARD, GLASS_HOVER,
                                                    )}
                                                >
                                                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                                                        <ArrowRight className="h-6 w-6 text-primary" />
                                                    </div>
                                                    <span className="text-sm font-bold text-primary uppercase tracking-wider">
                                                        +{totalImpacted - MAX_VISIBLE_IMPACTED} More
                                                    </span>
                                                </div>
                                            )}

                                            {/* Pending SKU cards — tracked but awaiting data */}
                                            {pendingSKUs.map((sku, idx) => (
                                                <div key={`pending-${idx}`} className="flex-shrink-0">
                                                    <PendingSKUCard sku={sku} brand={userBrand} />
                                                </div>
                                            ))}
                                        </SliderCarousel>

                                        {/* Expanded cards — shown inline below carousel */}
                                        {isImpactedExpanded && hasMoreImpacted && filteredOverview && (
                                            <div className="flex flex-wrap gap-4 mt-4 px-1">
                                                {filteredOverview.topImpactedSKUs.slice(MAX_VISIBLE_IMPACTED).map((sku) => (
                                                    <ImpactedSKUCompactCard
                                                        key={sku.id}
                                                        sku={sku}
                                                        mode={activeTab}
                                                        brand={userBrand}
                                                        onClick={() => setSelectedSKU(sku)}
                                                    />
                                                ))}
                                            </div>
                                        )}

                                        {isImpactedExpanded && hasMoreImpacted && (
                                            <div className="flex justify-center mt-3">
                                                <button
                                                    onClick={() => setIsImpactedExpanded(false)}
                                                    className="text-xs font-semibold text-primary hover:text-primary/80 uppercase tracking-wider transition-colors"
                                                >
                                                    Show Less
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex gap-4 py-4 px-1">
                                        {Array.from({ length: 4 }).map((_, i) => (
                                            <div key={i} className={cn("flex-shrink-0 w-[320px] min-h-[170px] flex flex-col items-center justify-center gap-2", GLASS_CARD, "opacity-60")}>
                                                <Package className="h-8 w-8 text-muted-foreground/30" />
                                                <span className="text-sm font-medium text-muted-foreground">No impacted SKUs</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </section>

                            {/* ========================================== */}
                            {/* CITIES (loads based on selected category)  */}
                            {/* ========================================== */}
                            <section className="pb-20">
                                <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
                                    <div>
                                        <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
                                            <MapPin className="w-6 h-6 text-primary" />
                                            Cities
                                        </h2>
                                        <p className="text-sm text-muted-foreground mt-1.5 font-medium">
                                            {isLoading && !filteredOverview
                                                ? 'Loading city data...'
                                                : selectedOverviewCategory
                                                    ? `Showing ${overviewCities.length} cities for ${selectedOverviewCategory}`
                                                    : `Showing ${overviewCities.length} cities — select a category to filter`
                                            }
                                        </p>
                                    </div>

                                </div>

                                {isLoading && !filteredOverview ? (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                                        {Array.from({ length: 8 }).map((_, i) => (
                                            <CityCardSkeleton key={i} />
                                        ))}
                                    </div>
                                ) : overviewCities.length > 0 ? (
                                    <div className={cn(
                                        "grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 transition-opacity duration-300",
                                        isRefetching && "opacity-50 grayscale-[20%] pointer-events-none"
                                    )}>
                                        {overviewCities.map((city) => (
                                            <CityCard key={city.id} city={city} onDrilldown={() => openCityDrilldown(city)} />
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-muted-foreground text-sm py-4">
                                        {selectedOverviewCategory ? 'No cities available for this category.' : 'No cities available.'}
                                    </p>
                                )}
                            </section>
                        </>
                    )}
                </div>
            </div>

            {/* SKU Detail Sidebar — opens on direct SKU click */}
            <SKUDetailSidebar
                isOpen={selectedSKU !== null}
                sku={selectedSKU}
                mode={activeTab}
                brand={userBrand}
                onClose={() => setSelectedSKU(null)}
                workspaceId={currentWorkspace?.id}
                platform={filters.platform}
                dateRange={filters.dateRange}
            />

            {/* City Drilldown Sidebar */}
            <DrilldownSidebar
                isOpen={isSidebarOpen}
                isLoading={sidebarLoading}
                data={sidebarData}
                city={sidebarCity}
                categoryName={selectedOverviewCategory || 'All Categories'}
                viewBy={viewBy}
                onViewByChange={setViewBy}
                onClose={closeDrilldown}
                brand={userBrand}
            />
        </>
    )
}
